// src/styles/theme.js
import { createTheme, alpha } from '@mui/material/styles';



// Create a temporary default theme to access its shadows
const defaultTheme = createTheme();

// Your custom shadows
const customShadows = [
    'none',
    '0px 2px 4px rgba(67, 160, 71, 0.06)',
    '0px 4px 8px rgba(67, 160, 71, 0.08)',
    '0px 8px 16px rgba(67, 160, 71, 0.08)',
    '0px 12px 24px rgba(67, 160, 71, 0.12)',
];

const theme = createTheme({
    palette: {
        primary: {
            light: '#66bb6a',
            main: '#43a047', // Deep Green
            dark: '#2e7d32',
            contrastText: '#fff',
        },
        secondary: {
            light: '#ffecb3',
            main: '#ffc400', // Refined Gold
            dark: '#ffab00',
            contrastText: '#212121',
        },
        background: {
            default: '#f8faf8', // Softer background
            paper: '#ffffff',
            gradient: 'linear-gradient(120deg, #e6f4ea 0%, #d4efdf 100%)',
            cardGradient: 'linear-gradient(135deg, #ffffff 0%, #f2f9f4 100%)',
        },
        success: {
            main: '#00b894', // Bright Eco Green
            light: '#66d4b1',
            dark: '#008b6f',
        },
        error: {
            main: '#e53935', // Refined red
            light: '#ff6f60',
            dark: '#ab000d',
        },
        info: {
            main: '#03a9f4',
            light: '#67daff',
            dark: '#007ac1',
        },
        text: {
            primary: '#1b5e20',
            secondary: '#546e7a',
            disabled: '#9e9e9e',
            hint: '#78909c',
        },
        divider: 'rgba(67, 160, 71, 0.12)',
    },
    shape: {
        borderRadius: 16,
    },
    typography: {
        fontFamily: '"Poppins", "Roboto", "Arial", sans-serif',
        h1: {
            fontWeight: 800,
            letterSpacing: '-0.025em',
        },
        h2: {
            fontWeight: 700,
            letterSpacing: '-0.025em',
        },
        h3: {
            fontWeight: 600,
            letterSpacing: '-0.025em',
        },
        h4: {
            fontWeight: 600,
            letterSpacing: '-0.01em',
        },
        h5: {
            fontWeight: 600,
        },
        h6: {
            fontWeight: 600,
        },
        subtitle1: {
            letterSpacing: '0.015em',
        },
        button: {
            fontWeight: 600,
            letterSpacing: '0.03em',
            textTransform: 'none',
        },
    },
    shadows: [
        ...customShadows,
        ...defaultTheme.shadows.slice(customShadows.length), // Use default shadows for higher levels

        // ...keep remaining shadows from Material UI
    ],
    components: {
        MuiCssBaseline: {
            styleOverrides: {
                body: {
                    backgroundImage: 'linear-gradient(145deg, #f8faf8 0%, #e6f4ea 100%)',
                    backgroundAttachment: 'fixed',
                    backgroundRepeat: 'no-repeat',
                    minHeight: '100vh',
                },
            },
        },
        MuiButton: {
            styleOverrides: {
                root: {
                    borderRadius: '28px',
                    padding: '10px 24px',
                    boxShadow: '0 4px 14px 0 rgba(67, 160, 71, 0.15)',
                    transition: 'all 0.25s ease-in-out',
                    '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: '0 6px 20px 0 rgba(67, 160, 71, 0.25)',
                    },
                },
                contained: {
                    background: 'linear-gradient(135deg, #4caf50 0%, #43a047 100%)',
                    '&:hover': {
                        background: 'linear-gradient(135deg, #66bb6a 0%, #4caf50 100%)',
                    },
                },
                containedSecondary: {
                    background: 'linear-gradient(135deg, #ffd600 0%, #ffc400 100%)',
                    '&:hover': {
                        background: 'linear-gradient(135deg, #ffea00 0%, #ffd600 100%)',
                    },
                },
            },
        },
        MuiCard: {
            styleOverrides: {
                root: {
                    borderRadius: '24px',
                    boxShadow: '0 8px 32px 0 rgba(67, 160, 71, 0.08)',
                    overflow: 'hidden',
                    transition: 'all 0.3s ease-in-out',
                    '&:hover': {
                        transform: 'translateY(-5px)',
                        boxShadow: '0 16px 40px 0 rgba(67, 160, 71, 0.12)',
                    },
                    background: 'linear-gradient(135deg, #ffffff 0%, #f5fbf7 100%)',
                },
            },
        },
        MuiPaper: {
            styleOverrides: {
                root: {
                    borderRadius: '24px',
                },
                elevation1: {
                    boxShadow: '0 4px 20px 0 rgba(67, 160, 71, 0.08)',
                },
                elevation2: {
                    boxShadow: '0 8px 24px 0 rgba(67, 160, 71, 0.1)',
                },
                elevation3: {
                    boxShadow: '0 12px 32px 0 rgba(67, 160, 71, 0.12)',
                },
                elevation4: {
                    boxShadow: '0 16px 40px 0 rgba(67, 160, 71, 0.14)',
                },
            },
        },
        MuiAppBar: {
            styleOverrides: {
                root: {
                    background: 'linear-gradient(90deg, #43a047 0%, #66bb6a 100%)',
                    boxShadow: '0 4px 20px rgba(67, 160, 71, 0.15)',
                },
            },
        },
        MuiTextField: {
            styleOverrides: {
                root: {
                    '& .MuiOutlinedInput-root': {
                        borderRadius: '16px',
                        '&.Mui-focused fieldset': {
                            borderColor: alpha('#43a047', 0.8),
                            boxShadow: `0 0 0 3px ${alpha('#43a047', 0.12)}`,
                        },
                    },
                },
            },
        },
    },
});

export default theme;